import { FACE_MESH_POINT_COUNT, LANDMARKS, clamp } from './helpers'
import {
  createPointArray,
  computeFaceMetrics,
  toCanonical,
  buildExpressionWeights
} from './faceMath'
import { getFaceMeshTriangles } from '../meshWarp'
import { centerCanonicalYaw } from './poseNormalization'

const buildDepthBias = (canonicalPoints, rig) => {
  const count = canonicalPoints?.length || 0
  const bias = new Float32Array(count)
  if (!count) return bias

  const nose = canonicalPoints[LANDMARKS.noseTip] || { x: 0, y: 0 }
  const scaleX = Math.max(0.2, rig?.faceMetrics?.faceW ? 0.45 : 0.45)
  const scaleY = Math.max(0.25, rig?.faceMetrics?.faceH ? 0.6 : 0.6)

  for (let i = 0; i < count; i += 1) {
    const p = canonicalPoints[i]
    const nx = (p.x - nose.x) / scaleX
    const ny = (p.y - nose.y) / scaleY
    const d = Math.hypot(nx, ny)
    const bowl = clamp(1 - d, -1, 1) * 0.08
    bias[i] = bowl
  }
  return bias
}

export async function buildAvatarRig({
  avatarImage,
  faceLandmarkerImage,
  debug,
  yawRecenter = true,
  yawRecenterStrength = 1,
  yawRecenterMaxDeg = 28
} = {}) {
  // Attempt to build rig when an image is present. Don't strictly require
  // `avatarImage.safe` because cross-origin images may still be usable by
  // MediaPipe detection even if canvas readback is disallowed. Detection
  // failures will be caught below and return null.
  if (!avatarImage?.img || !faceLandmarkerImage) return null
  try {
    const result = faceLandmarkerImage.detect(avatarImage.img)
    const landmarks = result?.faceLandmarks?.[0]
    if (!landmarks) return null

    const w = avatarImage.img.naturalWidth || avatarImage.img.width
    const h = avatarImage.img.naturalHeight || avatarImage.img.height

    const faceMetrics = computeFaceMetrics(landmarks, w, h)
    if (!faceMetrics) return null

    const meshCount = Math.min(landmarks.length, FACE_MESH_POINT_COUNT)
    const pointsPx = createPointArray(meshCount)
    for (let i = 0; i < meshCount; i += 1) {
      pointsPx[i].x = landmarks[i].x * w
      pointsPx[i].y = landmarks[i].y * h
    }

    const canonical = toCanonical(landmarks, faceMetrics, w, h, meshCount)
    const yawRecenterResult = centerCanonicalYaw(canonical, {
      enabled: yawRecenter !== false,
      strength: Number.isFinite(Number(yawRecenterStrength))
        ? Number(yawRecenterStrength)
        : 1,
      maxRadians:
        (Number.isFinite(Number(yawRecenterMaxDeg)) ? Number(yawRecenterMaxDeg) : 28) *
        (Math.PI / 180)
    })
    const triangles = getFaceMeshTriangles(meshCount)

    const point = (idx) => ({
      x: landmarks[idx].x * w,
      y: landmarks[idx].y * h
    })

    const eyeRect = (outerIdx, innerIdx, upperIdx, lowerIdx) => {
      const outer = point(outerIdx)
      const inner = point(innerIdx)
      const upper = point(upperIdx)
      const lower = point(lowerIdx)
      const left = Math.min(outer.x, inner.x)
      const right = Math.max(outer.x, inner.x)
      const top = Math.min(upper.y, lower.y)
      const bottom = Math.max(upper.y, lower.y)
      const padX = (right - left) * 0.4
      const padY = (bottom - top) * 0.6
      return {
        x: Math.max(0, left - padX),
        y: Math.max(0, top - padY),
        w: Math.min(w, right - left + padX * 2),
        h: Math.min(h, bottom - top + padY * 2)
      }
    }

    const mouthRect = () => {
      const left = point(LANDMARKS.mouthLeft)
      const right = point(LANDMARKS.mouthRight)
      const upper = point(LANDMARKS.mouthUpper)
      const lower = point(LANDMARKS.mouthLower)
      const padX = (right.x - left.x) * 0.35
      const padY = (lower.y - upper.y) * 0.8
      return {
        x: Math.max(0, left.x - padX),
        y: Math.max(0, upper.y - padY),
        w: Math.min(w, right.x - left.x + padX * 2),
        h: Math.min(h, lower.y - upper.y + padY * 2)
      }
    }

    const pickCanonical = (idx) => (idx < meshCount ? canonical[idx] : null)
    const mouthLeft = pickCanonical(LANDMARKS.mouthLeft)
    const mouthRight = pickCanonical(LANDMARKS.mouthRight)
    const mouthUpper = pickCanonical(LANDMARKS.mouthUpper)
    const mouthLower = pickCanonical(LANDMARKS.mouthLower)
    const leftEye = pickCanonical(LANDMARKS.leftEye)
    const rightEye = pickCanonical(LANDMARKS.rightEye)
    const leftEyeUpper = pickCanonical(LANDMARKS.leftEyeUpper)
    const leftEyeLower = pickCanonical(LANDMARKS.leftEyeLower)
    const rightEyeUpper = pickCanonical(LANDMARKS.rightEyeUpper)
    const rightEyeLower = pickCanonical(LANDMARKS.rightEyeLower)

    const mouthCenter =
      mouthLeft && mouthRight && mouthUpper && mouthLower
        ? { x: (mouthLeft.x + mouthRight.x) * 0.5, y: (mouthUpper.y + mouthLower.y) * 0.5 }
        : null
    const mouthRadiusX = mouthLeft && mouthRight ? Math.abs(mouthRight.x - mouthLeft.x) * 0.7 : 0
    const mouthRadiusY = mouthUpper && mouthLower ? Math.abs(mouthLower.y - mouthUpper.y) * 1.4 : 0

    const leftEyeCenter =
      leftEyeUpper && leftEyeLower && leftEye
        ? { x: leftEye.x, y: (leftEyeUpper.y + leftEyeLower.y) * 0.5 }
        : null
    const rightEyeCenter =
      rightEyeUpper && rightEyeLower && rightEye
        ? { x: rightEye.x, y: (rightEyeUpper.y + rightEyeLower.y) * 0.5 }
        : null
    const leftEyeRadiusX = leftEye && leftEyeUpper ? Math.abs(leftEye.x - leftEyeUpper.x) * 1.2 : 0
    const leftEyeRadiusY =
      leftEyeUpper && leftEyeLower ? Math.abs(leftEyeLower.y - leftEyeUpper.y) * 1.4 : 0
    const rightEyeRadiusX =
      rightEye && rightEyeUpper ? Math.abs(rightEye.x - rightEyeUpper.x) * 1.2 : 0
    const rightEyeRadiusY =
      rightEyeUpper && rightEyeLower ? Math.abs(rightEyeLower.y - rightEyeUpper.y) * 1.4 : 0

    const expressionWeights = buildExpressionWeights(canonical, {
      mouthCenter,
      mouthRadiusX,
      mouthRadiusY,
      leftEyeCenter,
      leftEyeRadiusX,
      leftEyeRadiusY,
      rightEyeCenter,
      rightEyeRadiusX,
      rightEyeRadiusY
    })
    const depthBias = buildDepthBias(canonical, { faceMetrics })

    if (debug)
      debug(
        `Avatar rig built (${meshCount} landmarks, ${Math.floor(triangles.length / 3)} tris, yawCenter=${(yawRecenterResult?.appliedYaw || 0).toFixed(3)})`
      )

    return {
      imageWidth: w,
      imageHeight: h,
      meshCount,
      pointsPx,
      canonical,
      triangles,
      faceMetrics,
      mouthCenter,
      mouthRadiusX,
      mouthRadiusY,
      leftEyeCenter,
      leftEyeRadiusX,
      leftEyeRadiusY,
      rightEyeCenter,
      rightEyeRadiusX,
      rightEyeRadiusY,
      expressionWeights,
      depthBias,
      yawRecenterResult,
      leftEye: eyeRect(
        LANDMARKS.leftEye,
        LANDMARKS.leftEyeInner,
        LANDMARKS.leftEyeUpper,
        LANDMARKS.leftEyeLower
      ),
      rightEye: eyeRect(
        LANDMARKS.rightEye,
        LANDMARKS.rightEyeInner,
        LANDMARKS.rightEyeUpper,
        LANDMARKS.rightEyeLower
      ),
      mouth: mouthRect()
    }
  } catch (err) {
    if (debug) debug('Avatar rig detection failed.')
    return null
  }
}

export default { buildAvatarRig }
